/**
 * "Drawing regions" editor for vector project modules: opt the module into
 * ROI-restricted drawing by naming the edit-bounding-box domain it uses.
 *
 * Stores the per-project override in `project_modules.config` via the
 * `PATCH /projects/:projectId/modules/:moduleId` endpoint.
 */

import { Button } from "@frontend/components/ui/button"
import { Input } from "@frontend/components/ui/input"
import { Label } from "@frontend/components/ui/label"
import { resolveVectorEditRegionDomain, vectorProjectConfigSchema } from "@shared/extensions/vector/schema"
import { useId, useMemo, useState } from "react"

import type { ProjectModuleItem } from "../../types"

interface ModuleEditRegionsProps {
  module: ProjectModuleItem
  onSave: (payload: { config: Record<string, unknown> | null }) => void
  isSaving: boolean
}

function parseJson(text: string | null): unknown {
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export function ModuleEditRegions({ module, onSave, isSaving }: ModuleEditRegionsProps) {
  if (module.template_type !== "vector") return null
  return (
    <VectorEditRegionsEditor key={module.config ?? "__default__"} module={module} onSave={onSave} isSaving={isSaving} />
  )
}

function VectorEditRegionsEditor({ module, onSave, isSaving }: ModuleEditRegionsProps) {
  const inputId = useId()
  const templateSchema = useMemo(() => parseJson(module.template_schema), [module.template_schema])
  const templateDomain = resolveVectorEditRegionDomain(templateSchema, null)
  const effective = resolveVectorEditRegionDomain(templateSchema, parseJson(module.config))
  const [draft, setDraft] = useState(effective ?? "")
  const trimmed = draft.trim()
  const valid = trimmed === "" || vectorProjectConfigSchema.safeParse({ edit_region_domain: trimmed }).success
  const unchanged = trimmed === (effective ?? "")

  const handleSave = () => {
    // Matching the template needs no override (it then follows later template changes);
    // empty while the template opts in = explicitly off for this project.
    if (trimmed === (templateDomain ?? "")) {
      onSave({ config: null })
      return
    }
    onSave({ config: { edit_region_domain: trimmed === "" ? null : trimmed } })
  }

  return (
    <div className="mt-4 rounded-lg border p-3">
      <p className="text-sm font-semibold">Drawing regions</p>
      <p className="text-xs text-muted-foreground mt-1">
        Limit drawing to the slide rectangles tagged for this module. Leave empty to allow drawing anywhere.
      </p>
      <div className="mt-3 space-y-1.5">
        <Label htmlFor={inputId} className="text-xs">
          Module tag
        </Label>
        <Input
          id={inputId}
          value={draft}
          placeholder="e.g. vector_roi"
          onChange={(event) => setDraft(event.target.value)}
          className="h-8 text-xs"
        />
        <p className="text-[11px] text-muted-foreground">
          Matches each rectangle&rsquo;s <code>domain</code>. Untagged rectangles always apply.
          {templateDomain && <> Template default: &ldquo;{templateDomain}&rdquo;.</>}
        </p>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={isSaving || !valid || unchanged}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setDraft(templateDomain ?? "")
            onSave({ config: null })
          }}
          disabled={isSaving || module.config === null}
        >
          Reset to template default
        </Button>
      </div>
    </div>
  )
}
